/**
 * EnhancedLoginPage.jsx
 * 통합 검증 시스템이 적용된 관리자 로그인 페이지
 * SI-05 Phase 8: 새로운 검증 체계 통합 구현
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuthValidation, useSecurityMonitor } from '../../hooks/useValidation';
import { authSchemas } from '../../utils/validationSchemas';
import { sanitizeApiRequest } from '../../utils/xssProtection';
import getMessage from '../../utils/messages';
import { openAlert, openConfirm } from '../../utils/common';
import '../../assets/css/login.css';
import '../../assets/css/enhanced-login.css';

const EnhancedLoginPage = () => {
  const [conflictInfo, setConflictInfo] = useState(null);
  const [forceError, setForceError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);
  
  const navigate = useNavigate();
  const { login, isAuthenticated, role: authRole } = useAuth();
  
  // 통합 검증 훅 사용
  const {
    adminId,
    password,
    errors,
    securityEvents,
    setAdminId,
    setPassword,
    validateLoginData,
    resetAuthForm,
    isValid,
    hasSecurityRisks
  } = useAuthValidation();

  // 보안 모니터링 훅
  const {
    securityLog,
    riskLevel,
    logSecurityEvent,
    clearSecurityLog,
    eventCount
  } = useSecurityMonitor();


  const postLoginHome = (role) => (
    role === 'calibration_operator' ? '/calibration/queue' : '/settings/dashboard'
  );

  // 인증 상태가 변경될 때 페이지 리디렉션
  useEffect(() => {
    if (isAuthenticated) {
      navigate(postLoginHome(authRole), { replace: true });
    }
  }, [isAuthenticated, authRole, navigate]);

  // 보안 이벤트 모니터링
  useEffect(() => {
    if (securityEvents.length > 0) {
      const latestEvent = securityEvents[securityEvents.length - 1];
      logSecurityEvent(latestEvent);
      
      // HIGH 위험도 시 경고 표시
      if (latestEvent.riskLevel === 'HIGH') {
        setShowSecurityWarning(true);
      }
    }
  }, [securityEvents, logSecurityEvent]);

  /**
   * 로그인 처리 (통합 검증 적용)
   */
  const handleLogin = async () => {
    // 1. 클라이언트 검증
    const validation = validateLoginData();
    
    if (!validation.isValid) {
      // 검증 실패 시 첫 번째 오류 메시지 표시
      const firstError = Object.values(validation.errors)[0];
      openAlert({ title: '입력 오류', description: firstError });
      return;
    }

    // 2. 보안 위험 감지 시 추가 확인
    if (hasSecurityRisks && riskLevel === 'HIGH') {
      const confirmResult = window.confirm(
        '입력 데이터에서 보안 위험이 감지되었습니다. 계속 진행하시겠습니까?'
      );
      if (!confirmResult) {
        return;
      }
    }

    setIsLoading(true);
    
    try {
      // 3. API 요청 데이터 정화
      const safeLoginData = sanitizeApiRequest(validation.data, '/auth/login');
      
      // 4. 보안 이벤트 로깅
      if (validation.securityEvents.length > 0) {
        console.warn('Login attempt with security events:', {
          eventCount: validation.securityEvents.length,
          riskLevel,
          adminId: safeLoginData.adminId
        });
      }

      // 5. 실제 로그인 시도
      const result = await login(safeLoginData.adminId, safeLoginData.password);
      
      if (!result.success) {
        if (result.multiLogin && result.sessionInfo) {
          setConflictInfo(result.sessionInfo);
        } else {
          // 메시지 코드 기반 처리
          let description = result.message;
          
          if (result.messageCode && result.messageParams) {
            description = getMessage(result.messageCode, result.messageParams);
          }
          
          openAlert({ 
            title: '로그인 실패', 
            description: description || '로그인에 실패했습니다.',
            variant: 'error'
          });

          // 실패 시 보안 이벤트 로깅
          logSecurityEvent({
            type: 'LOGIN_FAILURE',
            field: 'login',
            riskLevel: 'MEDIUM',
            message: 'Login attempt failed',
            adminId: safeLoginData.adminId
          });
        }
      } else {
        // 6. 로그인 성공 처리
        console.info('Login successful with enhanced validation');
        
        // 성공 시 보안 로그 정리
        clearSecurityLog();
        resetAuthForm();
        
        // 초기 비밀번호 체크 (최우선)
        if (result.data && result.data.must_change_password && result.data.password_change_reason === 'initial_password') {
          // 초기 비밀번호인 경우 비밀번호 변경 강제
          navigate('/password-expired', { 
            state: { 
              isInitialPassword: true,
              isExpired: false,
              canSkip: false,
              message: '관리자가 생성한 초기 비밀번호입니다. 보안을 위해 비밀번호를 변경해주세요.'
            } 
          });
          return;
        }
        
        // 비밀번호 만료 체크 및 리다이렉트
        if (result.data && result.data.password_expired) {
          // 비밀번호가 만료된 경우 비밀번호 변경 페이지로 리다이렉트
          navigate('/password-expired', { 
            state: { 
              isExpired: true,
              daysUntilExpiry: result.data.days_until_password_expiry,
              canSkip: false 
            } 
          });
          return;
        } else if (result.data && result.data.password_expiry_warning) {
          // 비밀번호가 곧 만료되는 경우 경고와 함께 비밀번호 변경 페이지 옵션 제공
          const daysLeft = result.data.days_until_password_expiry;
          
          // 비밀번호 만료 상태 데이터
          const expiryState = {
            isExpired: false,
            daysUntilExpiry: daysLeft,
            canSkip: true
          };
          
          // openConfirm 호출 직전에 window 함수 등록 (클로저로 데이터 캡처)
          window.handlePasswordChangeNavigate = () => {
            navigate('/password-expired', { state: expiryState });
          };
          
          openConfirm({
            title: '비밀번호 만료 임박',
            description: `비밀번호가 ${daysLeft}일 후 만료됩니다.\n지금 비밀번호를 변경하시겠습니까?`,
            positive_method: 'handlePasswordChangeNavigate',
            positive_param: {},
            negative_method: null,
            negative_param: null,
            positive_btn: '변경하기',
            negative_btn: '나중에'
          });
          return;
        }
        
        // 로그인 성공 - 대시보드로 이동
        // 시스템 알림은 App.js의 SystemAlertDisplay 컴포넌트가 자동으로 표시
        navigate(postLoginHome(result.data?.role || authRole), { replace: true });
      }
    } catch (error) {
      console.error('Enhanced login error:', error);
      
      // 오류 시 보안 이벤트 로깅
      logSecurityEvent({
        type: 'LOGIN_ERROR',
        field: 'login',
        riskLevel: 'HIGH',
        message: 'Login process error',
        error: error.message
      });
      
      openAlert({ 
        title: '시스템 오류', 
        description: '로그인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 엔터 키 처리
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isValid && !isLoading) {
      handleLogin();
    }
  };

  /**
   * 강제 로그인 처리
   */
  const handleForceLogin = async () => {
    const validation = validateLoginData();
    
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      openAlert({ title: '입력 오류', description: firstError });
      return;
    }

    try {
      setIsLoading(true);
      
      const safeLoginData = sanitizeApiRequest(validation.data, '/auth/login');
      const result = await login(safeLoginData.adminId, safeLoginData.password, { forceLogin: true });
      
      if (result.success) {
        setConflictInfo(null);
        clearSecurityLog();
        navigate(postLoginHome(result.data?.role || authRole), { replace: true });
      } else if (result.multiLogin) {
        setConflictInfo(result.sessionInfo);
        setForceError('강제 로그인을 시도했으나 여전히 다른 세션이 발견되었습니다.');
      } else {
        setForceError(result.message || '강제 로그인 실패');
      }
    } catch (error) {
      console.error('Force login error:', error);
      setForceError('강제 로그인 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 보안 경고 닫기
   */
  const handleCloseSecurityWarning = () => {
    setShowSecurityWarning(false);
  };

  /**
   * 입력 필드 스타일 (보안 위험 시 강조)
   */
  const getInputClassName = (fieldName) => {
    let className = '';
    
    if (errors[fieldName]) {
      className += ' error';
    }
    
    if (securityEvents.some(event => event.field === fieldName && event.riskLevel === 'HIGH')) {
      className += ' security-risk';
    }
    
    return className;
  };

  return (
    <div className="wrap">
      <div className="login_wrap">
        <div className="login_box">
          <div className="login_logo">
            <img src="/adm/images/login_logo.png" alt="logo" />
          </div>

          {/* 보안 경고 표시 */}
          {showSecurityWarning && (
            <div className="security_warning">
              <div className="warning_content">
                <strong>⚠️ 보안 경고</strong>
                <p>입력 데이터에서 의심스러운 패턴이 감지되었습니다.</p>
                <p>
                  보안 이벤트: {eventCount}건, 위험도: {riskLevel}
                </p>
                <button
                  onClick={handleCloseSecurityWarning}
                  className="close_warning"
                >
                  확인
                </button>
              </div>
            </div>
          )}

          <div className="login_input">
            {/* SI-05: 관리자 ID 입력 (4~20자) */}
            <div className="input_group">
              <input
                type="text"
                placeholder="아이디 입력"
                autoComplete="off"
                value={adminId}
                onChange={(e) => setAdminId(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={20}
                className={getInputClassName("adminId")}
                disabled={isLoading}
              />
              {errors.adminId && (
                <div className="error_message">{errors.adminId}</div>
              )}
            </div>

            {/* SI-05: 비밀번호 입력 (8~50자) */}
            <div className="input_group">
              <input
                type="password"
                placeholder="비밀번호 입력"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={50}
                className={getInputClassName("password")}
                disabled={isLoading}
              />
              {errors.password && (
                <div className="error_message">{errors.password}</div>
              )}
            </div>

            {/* 로그인 버튼 */}
            {conflictInfo ? (
              <button
                type="button"
                className="login_btn force_login"
                onClick={handleForceLogin}
                disabled={isLoading}
              >
                {isLoading ? "처리 중..." : "강제 로그아웃 후 로그인"}
              </button>
            ) : (
              <button
                type="button"
                className={`login_btn`}
                onClick={handleLogin}
                disabled={isLoading}
              >
                {isLoading ? "로그인 중..." : "관리자 로그인"}
              </button>
            )}
          </div>

          {/* 다중 로그인 충돌 정보 */}
          {conflictInfo && (
            <div className="conflict_info">
              <p>이미 다른 기기에서 로그인 중입니다.</p>
              <p>로그인 시간: {conflictInfo.created_at}</p>
              <p>마지막 활동 시간: {conflictInfo.last_active}</p>
              {forceError && <div className="error_msg">{forceError}</div>}
            </div>
          )}

          {/* 보안 상태 표시 (개발 모드에서만) */}
          {process.env.NODE_ENV === "development" &&
            (eventCount > 0 || hasSecurityRisks) && (
              <div className="security_status">
                <div className="security_info">
                  <span className={`risk_level ${riskLevel.toLowerCase()}`}>
                    위험도: {riskLevel}
                  </span>
                  <span className="event_count">
                    보안 이벤트: {eventCount}건
                  </span>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedLoginPage;
